import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';

/**
 * A custom element that allows the user to select a quantity.
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement} quantityInput
 *
 * @extends {Component<Refs>}
 */
class QuantitySelectorComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  /**
   * Handles a variant change by updating the input's min/max/step/value attributes
   * from the freshly rendered HTML returned by the variant fetch.
   * @param {import('@theme/events').VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    const productForm = this.closest('product-form-component');
    if (!productForm) return;

    if (!(productForm instanceof HTMLElement)) return;
    if (event.detail.data.productId !== productForm.dataset.productId) return;

    const newProductForm = event.detail.data.html.querySelector(
      `product-form-component[data-product-id="${productForm.dataset.productId}"]`
    );
    const newInput = newProductForm?.querySelector('quantity-selector-component input[type="number"]');
    if (!newInput) return;

    const { quantityInput } = this.refs;
    const newMin = newInput.getAttribute('min');
    const newMax = newInput.getAttribute('max');
    const newStep = newInput.getAttribute('step') ?? '1';

    if (newMin !== null) {
      quantityInput.setAttribute('min', newMin);
    } else {
      quantityInput.removeAttribute('min');
    }

    if (newMax !== null) {
      quantityInput.setAttribute('max', newMax);
    } else {
      quantityInput.removeAttribute('max');
    }

    quantityInput.setAttribute('step', newStep);
    quantityInput.value = newMin ?? '1';
  };

  /**
   * Handles the quantity increase event.
   * @param {Event} event - The event.
   */
  increaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    this.refs.quantityInput.stepUp();
    this.#onQuantityChange();
  }

  /**
   * Handles the quantity decrease event.
   * @param {Event} event - The event.
   */
  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    this.refs.quantityInput.stepDown();
    this.#onQuantityChange();
  }

  /**
   * When our input gets focused, we want to fully select the value.
   * @param {FocusEvent} event
   */
  selectInputValue(event) {
    const { quantityInput } = this.refs;
    if (!(event.target instanceof HTMLInputElement) || document.activeElement !== quantityInput) return;

    quantityInput.select();
  }

  /**
   * Handles the quantity set event.
   * @param {Event} event - The event.
   */
  setQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    if (event.target instanceof HTMLInputElement) {
      this.refs.quantityInput.value = event.target.value;
    }
    this.#onQuantityChange();
  }

  /**
   * Handles the quantity change event.
   */
  #onQuantityChange() {
    const { quantityInput } = this.refs;

    this.#checkQuantityRules();
    const newValue = parseInt(quantityInput.value);

    quantityInput.dispatchEvent(new QuantitySelectorUpdateEvent(newValue, Number(quantityInput.dataset.cartLine)));
  }

  /**
   * Checks the quantity rules are met, enforcing min, max and step alignment.
   * All comparisons are done numerically to avoid string-comparison bugs
   * (e.g. "20" < "3" being true in JS).
   */
  #checkQuantityRules = () => {
    const { quantityInput } = this.refs;
    const minAttr = parseInt(quantityInput.min, 10);
    const min = isNaN(minAttr) ? 1 : minAttr;
    const max = quantityInput.max ? parseInt(quantityInput.max, 10) : Infinity;
    const step = parseInt(quantityInput.step, 10) || 1;
    let value = parseInt(quantityInput.value, 10);

    if (isNaN(value) || value < min) value = min;
    if (value > max) value = max;

    const steps = Math.round((value - min) / step);
    value = min + steps * step;

    if (value > max) value = min + Math.floor((max - min) / step) * step;

    quantityInput.value = String(value);
  };

  /**
   * Gets the quantity input.
   * @returns {HTMLInputElement} The quantity input.
   */
  get quantityInput() {
    if (!this.refs.quantityInput) {
      throw new Error('Missing <input ref="quantityInput" /> inside <quantity-selector-component />');
    }

    return this.refs.quantityInput;
  }
}

if (!customElements.get('quantity-selector-component')) {
  customElements.define('quantity-selector-component', QuantitySelectorComponent);
}
